import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  Autocomplete,
  CircularProgress,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  UploadFile as UploadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AssignmentReturn as ReturnIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckCircleIcon,
  Inventory2Outlined as InventoryIcon,
  SwapHorizOutlined as RtoIcon,
  CurrencyRupee as RefundIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  importMyntraReturnReport,
  getMyntraReturnReasonAnalysis,
  getMyntraReturnReportList,
  getMyntraReturnReportSkuList,
} from "app/redux/slice/myntraSlice";
import { usePagination } from "app/hooks/usePagination";
import { Table as CustomTable } from "app/components";

// ── Theme constants ────────────────────────────────────────────────────────

const MYNTRA_PINK = "#FF3F6C";
const CHART_COLORS = [
  "#FF3F6C", "#9C27B0", "#FF9800", "#3F51B5", "#009688",
  "#F44336", "#4CAF50", "#2196F3", "#795548", "#607D8B",
];

// ── Helpers ────────────────────────────────────────────────────────────────

const fmtDate = (v) => (v ? moment.utc(v).format("DD/MM/YYYY") : "—");

const shortLabel = (text = "", max = 22) =>
  text.length > max ? text.slice(0, max) + "…" : text;

// ── Sub-components ─────────────────────────────────────────────────────────

const ImportResultBanner = ({ result }) => {
  if (!result) return null;
  const severity =
    result.status === "completed"
      ? "success"
      : result.status === "partial"
        ? "warning"
        : "error";
  return (
    <Alert severity={severity} icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
      <strong>{result.fileName}</strong> — {result.inserted} new &nbsp;·&nbsp;
      {result.updated} updated &nbsp;·&nbsp;
      {result.withinFileDuplicates} in-file duplicates &nbsp;·&nbsp;
      {result.skipped} skipped &nbsp;(total rows: {result.totalRows})
    </Alert>
  );
};

const SummaryCard = ({ label, value, color, icon: Icon }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      height: "100%",
      border: `1px solid ${color}30`,
      borderLeft: `4px solid ${color}`,
      borderRadius: 2,
      display: "flex",
      alignItems: "center",
      gap: 2,
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        backgroundColor: `${color}18`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ color, fontSize: 22 }} />
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h4" fontWeight="bold" sx={{ color, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const HorizTick = ({ x, y, payload }) => {
  const label = shortLabel(payload.value, 26);
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fill="#666">
      {label}
    </text>
  );
};

// ── Main component ─────────────────────────────────────────────────────────

const MyntraReturnReasonAnalysis = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);

  const { returnReport, returnReasonAnalysis, returnReportSkuList } =
    useSelector((s) => s.myntra);

  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // ── Shared SKU search (applied to both tabs via API) ──────────────────────
  const [skuInput, setSkuInput] = useState("");           // Autocomplete text
  const [appliedSku, setAppliedSku] = useState(null);    // committed to API
  const [statusFilter, setStatusFilter] = useState("");

  // ── Records tab full-text search (debounced, API-backed) ─────────────────
  const [recordSearch, setRecordSearch] = useState("");
  const [debouncedRecordSearch, setDebouncedRecordSearch] = useState("");

  const [tab, setTab] = useState(0);

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "myntra-return-report-list" });

  // ── Debounce record search ─────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setDebouncedRecordSearch(recordSearch), 400);
    return () => clearTimeout(t);
  }, [recordSearch]);

  // ── SKU list (once) ────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(getMyntraReturnReportSkuList());
  }, [dispatch]);

  // ── Main data — re-fetches whenever SKU / status / page / search changes ──
  useEffect(() => {
    dispatch(
      getMyntraReturnReasonAnalysis({
        sellerSkuCode: appliedSku || "",
        status: statusFilter,
        search: appliedSku ? "" : debouncedRecordSearch,
      })
    );
    dispatch(
      getMyntraReturnReportList({
        page,
        perPage: rowsPerPage,
        sellerSkuCode: appliedSku || "",
        status: statusFilter,
        search: debouncedRecordSearch,
      })
    );
  }, [dispatch, appliedSku, statusFilter, page, rowsPerPage, debouncedRecordSearch]);

  // ── File upload ────────────────────────────────────────────────────────────

  const refreshAll = useCallback(() => {
    dispatch(getMyntraReturnReportSkuList());
    dispatch(
      getMyntraReturnReasonAnalysis({
        sellerSkuCode: appliedSku || "",
        status: statusFilter,
        search: appliedSku ? "" : debouncedRecordSearch,
      })
    );
    dispatch(
      getMyntraReturnReportList({
        page,
        perPage: rowsPerPage,
        sellerSkuCode: appliedSku || "",
        status: statusFilter,
        search: debouncedRecordSearch,
      })
    );
  }, [dispatch, appliedSku, statusFilter, page, rowsPerPage, debouncedRecordSearch]);

  const handleFile = useCallback(
    (file) => {
      if (!file) return;
      if (!file.name.toLowerCase().endsWith(".csv")) {
        enqueueSnackbar("Only CSV files are supported.", { variant: "error" });
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      setUploading(true);
      setImportResult(null);

      dispatch(importMyntraReturnReport(formData))
        .unwrap()
        .then((res) => {
          const d = res?.data?.data;
          if (res?.data?.statusCode === 200 && d) {
            setImportResult(d);
            enqueueSnackbar(
              `Import ${d.status}: ${d.inserted} inserted, ${d.updated} updated.`,
              { variant: d.status === "completed" ? "success" : "warning" }
            );
            refreshAll();
          } else {
            enqueueSnackbar(res?.data?.responseMessage || "Import failed.", {
              variant: "error",
            });
          }
        })
        .catch(() =>
          enqueueSnackbar("Upload failed. Please try again.", {
            variant: "error",
          })
        )
        .finally(() => {
          setUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = "";
        });
    },
    [dispatch, enqueueSnackbar, refreshAll]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      handleFile(e.dataTransfer.files?.[0]);
    },
    [handleFile]
  );

  // ── Filter helpers ─────────────────────────────────────────────────────────

  // Called when user picks a SKU from the dropdown or presses Enter
  const applySkuSearch = (sku) => {
    setAppliedSku(sku || null);
    handleChangePage(0, 0);
  };

  const clearAllFilters = () => {
    setSkuInput("");
    setAppliedSku(null);
    setStatusFilter("");
    setRecordSearch("");
    setDebouncedRecordSearch("");
    handleChangePage(0, 0);
  };

  const hasActiveFilter = Boolean(appliedSku || statusFilter || debouncedRecordSearch);

  // ── Derived ────────────────────────────────────────────────────────────────

  const { summary, reasonBreakdown, skuBreakdown } = returnReasonAnalysis;

  const skuOptions = (returnReportSkuList.skus || []).map(
    (s) => s.sellerSkuCode || s.myntraSkuCode
  );

  const pieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const { reason, count, sharePercent } = payload[0].payload;
    return (
      <Paper sx={{ p: 1.5, fontSize: 12, border: "1px solid #eee" }}>
        <Typography variant="body2" fontWeight="bold">
          {reason}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {count} returns &nbsp;·&nbsp; {sharePercent}%
        </Typography>
      </Paper>
    );
  };

  // ── Records table columns ──────────────────────────────────────────────────

  const columns = [
    { id: "orderId", label: "Order ID", sort: false },
    { id: "sellerSkuCode", label: "Seller SKU", sort: false },
    {
      id: "returnCreatedDate",
      label: "Return Date",
      sort: false,
      render: (row) => fmtDate(row.returnCreatedDate),
    },
    { id: "returnReason", label: "Return Reason", sort: false },
    { id: "returnMode", label: "Mode", sort: false },
    {
      id: "status",
      label: "Status",
      sort: false,
      render: (row) => (
        <Chip
          label={row.status || "—"}
          size="small"
          color={row.status === "RTO" ? "warning" : "error"}
          variant="outlined"
        />
      ),
    },
    {
      id: "productId",
      label: "Product",
      sort: false,
      render: (row) =>
        row.productId?.name ? (
          <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
            {row.productId.name}
            {row.variantId?.variantCode ? ` / ${row.variantId.variantCode}` : ""}
          </Typography>
        ) : (
          <Chip label="Unlinked" size="small" variant="outlined" />
        ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Page header ── */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: `${MYNTRA_PINK}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AnalyticsIcon sx={{ color: MYNTRA_PINK, fontSize: 24 }} />
        </Box>
        <Box>
          <Typography variant="h4" fontWeight="bold" lineHeight={1.2}>
            Return Reason Analysis
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Upload Myntra return CSVs and analyse return reasons by SKU
          </Typography>
        </Box>
      </Stack>

      {/* ── Upload card ── */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardHeader
          title="Upload Returns CSV"
          titleTypographyProps={{ variant: "subtitle1", fontWeight: "bold" }}
          sx={{ pb: 0 }}
        />
        <CardContent>
          <Box
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => !uploading && fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${uploading ? "#ccc" : `${MYNTRA_PINK}50`}`,
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              cursor: uploading ? "not-allowed" : "pointer",
              backgroundColor: uploading ? "#fafafa" : `${MYNTRA_PINK}06`,
              transition: "background 0.2s, border-color 0.2s",
              "&:hover": {
                backgroundColor: uploading ? "#fafafa" : `${MYNTRA_PINK}12`,
              },
            }}
          >
            {uploading ? (
              <Stack alignItems="center" spacing={1.5}>
                <CircularProgress size={32} sx={{ color: MYNTRA_PINK }} />
                <Typography variant="body2" color="text.secondary">
                  Uploading & importing…
                </Typography>
                <LinearProgress sx={{ width: "50%", borderRadius: 4 }} />
              </Stack>
            ) : (
              <Stack alignItems="center" spacing={0.5}>
                <UploadIcon sx={{ fontSize: 40, color: MYNTRA_PINK }} />
                <Typography variant="body1" fontWeight="bold">
                  Drag & drop a CSV here, or click to browse
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supports Myntra Returns Report CSV &nbsp;·&nbsp; Max 10 MB
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Duplicate <code>order_line_id</code> rows are detected and
                  skipped automatically
                </Typography>
              </Stack>
            )}
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <ImportResultBanner result={importResult} />
        </CardContent>
      </Card>

      {/* ── Search & filter bar ── */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="flex-start">

            {/* SKU search — shared across both tabs, hits the API */}
            <Grid item xs={12} sm={5} md={4}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5, fontWeight: 500 }}
              >
                SKU Search &nbsp;
                <span style={{ fontWeight: 400, opacity: 0.7 }}>
                  — filters Analysis &amp; Records
                </span>
              </Typography>
              <Autocomplete
                options={skuOptions}
                value={appliedSku}
                inputValue={skuInput}
                onInputChange={(_, v) => setSkuInput(v)}
                onChange={(_, v) => applySkuSearch(v)}
                loading={returnReportSkuList.loading}
                size="small"
                clearOnEscape
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Type or select a SKU…"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon
                            sx={{ color: "text.disabled", fontSize: 18, mr: 0.5 }}
                          />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applySkuSearch(skuInput || null);
                    }}
                  />
                )}
              />
            </Grid>

            {/* Status filter */}
            <Grid item xs={12} sm={3} md={2}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5, fontWeight: 500 }}
              >
                Status
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={statusFilter}
                  displayEmpty
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    handleChangePage(0, 0);
                  }}
                >
                  <MenuItem value="">All statuses</MenuItem>
                  <MenuItem value="Return">Return</MenuItem>
                  <MenuItem value="RTO">RTO</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Full-text search — always visible, filters both Analysis & Records */}
            <Grid item xs={12} sm={5} md={4}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5, fontWeight: 500 }}
              >
                Search &nbsp;
                <span style={{ fontWeight: 400, opacity: 0.7 }}>
                  — filters Analysis &amp; Records
                </span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="SKU, Order ID, reason, brand…"
                value={recordSearch}
                onChange={(e) => {
                  setRecordSearch(e.target.value);
                  handleChangePage(0, 0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                  endAdornment: recordSearch ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => { setRecordSearch(""); setDebouncedRecordSearch(""); }}
                      >
                        <ClearIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>

            {/* Clear button */}
            <Grid item xs={12} sm="auto" sx={{ mt: { xs: 0, sm: 2.8 } }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={clearAllFilters}
                disabled={
                  !appliedSku && !statusFilter && !skuInput &&
                  !recordSearch && !debouncedRecordSearch
                }
              >
                Clear
              </Button>
            </Grid>

            {/* Active filter chips */}
            {hasActiveFilter && (
              <Grid item xs={12}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {appliedSku && (
                    <Chip
                      label={`SKU: ${appliedSku}`}
                      onDelete={() => { setAppliedSku(null); setSkuInput(""); handleChangePage(0, 0); }}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {statusFilter && (
                    <Chip
                      label={`Status: ${statusFilter}`}
                      onDelete={() => { setStatusFilter(""); handleChangePage(0, 0); }}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                  {debouncedRecordSearch && (
                    <Chip
                      label={`"${debouncedRecordSearch}"`}
                      onDelete={() => { setRecordSearch(""); setDebouncedRecordSearch(""); }}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* ── Tabs ── */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          TabIndicatorProps={{ style: { backgroundColor: MYNTRA_PINK } }}
        >
          <Tab
            label="Analysis"
            sx={{ fontWeight: tab === 0 ? "bold" : "normal" }}
          />
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <span>Records</span>
                {returnReport.total > 0 && (
                  <Chip
                    label={returnReport.total}
                    size="small"
                    sx={{ height: 18, fontSize: 11, pointerEvents: "none" }}
                  />
                )}
              </Stack>
            }
            sx={{ fontWeight: tab === 1 ? "bold" : "normal" }}
          />
        </Tabs>
      </Box>

      {/* ══════════════ ANALYSIS TAB ══════════════ */}
      {tab === 0 && (
        <>
          {returnReasonAnalysis.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: MYNTRA_PINK }} />
            </Box>
          ) : (
            <>
              {/* Summary cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <SummaryCard
                    label="Total Records"
                    value={summary?.total ?? 0}
                    color="#3F51B5"
                    icon={InventoryIcon}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <SummaryCard
                    label="Returns"
                    value={summary?.returned ?? 0}
                    color={MYNTRA_PINK}
                    icon={ReturnIcon}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <SummaryCard
                    label="RTO"
                    value={summary?.rto ?? 0}
                    color="#FF9800"
                    icon={RtoIcon}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <SummaryCard
                    label="Refunded"
                    value={summary?.refunded ?? 0}
                    color="#4CAF50"
                    icon={RefundIcon}
                  />
                </Grid>
              </Grid>

              {reasonBreakdown.length > 0 ? (
                <>
                  {/* Charts row */}
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={7}>
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardHeader
                          title="Return Reasons — Frequency"
                          titleTypographyProps={{ variant: "subtitle1", fontWeight: "bold" }}
                          sx={{ pb: 0 }}
                        />
                        <CardContent>
                          <ResponsiveContainer
                            width="100%"
                            height={Math.max(260, reasonBreakdown.length * 36)}
                          >
                            <BarChart
                              data={reasonBreakdown}
                              layout="vertical"
                              margin={{ top: 4, right: 30, left: 10, bottom: 4 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                              <YAxis
                                type="category"
                                dataKey="reason"
                                width={170}
                                tick={<HorizTick />}
                              />
                              <RechartsTooltip formatter={(value) => [value, "Count"]} />
                              <Bar
                                dataKey="count"
                                fill={MYNTRA_PINK}
                                radius={[0, 4, 4, 0]}
                                label={{ position: "right", fontSize: 11, fill: "#555" }}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardHeader
                          title="Return Reasons — Share"
                          titleTypographyProps={{ variant: "subtitle1", fontWeight: "bold" }}
                          sx={{ pb: 0 }}
                        />
                        <CardContent>
                          <ResponsiveContainer width="100%" height={340}>
                            <PieChart>
                              <Pie
                                data={reasonBreakdown}
                                dataKey="count"
                                nameKey="reason"
                                cx="50%"
                                cy="45%"
                                outerRadius={100}
                                label={({ percent }) =>
                                  percent > 0.04 ? `${(percent * 100).toFixed(0)}%` : ""
                                }
                                labelLine={false}
                              >
                                {reasonBreakdown.map((_, idx) => (
                                  <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <RechartsTooltip content={pieTooltip} />
                              <Legend
                                iconSize={10}
                                formatter={(v) => v.length > 24 ? v.slice(0, 24) + "…" : v}
                                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Reason summary table */}
                  <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                    <CardHeader
                      title="Return Reason Summary"
                      titleTypographyProps={{ variant: "subtitle1", fontWeight: "bold" }}
                      sx={{ pb: 0 }}
                    />
                    <CardContent sx={{ pt: 1 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: `${MYNTRA_PINK}10` }}>
                              <TableCell sx={{ width: 40, fontWeight: "bold" }}>#</TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>Return Reason</TableCell>
                              <TableCell align="right" sx={{ fontWeight: "bold", width: 80 }}>Count</TableCell>
                              <TableCell align="right" sx={{ fontWeight: "bold", width: 90 }}>% Share</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {reasonBreakdown.map((row, i) => (
                              <TableRow
                                key={i}
                                hover
                                sx={{ backgroundColor: i === 0 ? `${MYNTRA_PINK}06` : undefined }}
                              >
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">{i + 1}</Typography>
                                </TableCell>
                                <TableCell>
                                  {i === 0 ? (
                                    <Chip
                                      label={row.reason}
                                      size="small"
                                      sx={{
                                        backgroundColor: `${MYNTRA_PINK}18`,
                                        color: MYNTRA_PINK,
                                        fontWeight: "bold",
                                        border: `1px solid ${MYNTRA_PINK}40`,
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="body2">{row.reason}</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight="bold">{row.count}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" color="text.secondary">{row.sharePercent}%</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>

                  {/* SKU-wise breakdown */}
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardHeader
                      title={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ReturnIcon sx={{ color: MYNTRA_PINK, fontSize: 20 }} />
                          <span>SKU-wise Return Reason Breakdown</span>
                          <Chip
                            label={`${skuBreakdown.length} SKU${skuBreakdown.length !== 1 ? "s" : ""}`}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 0.5 }}
                          />
                        </Stack>
                      }
                      titleTypographyProps={{ variant: "subtitle1", fontWeight: "bold" }}
                      sx={{ pb: 0 }}
                    />
                    <CardContent sx={{ pt: 1 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: `${MYNTRA_PINK}10` }}>
                              {["Seller SKU", "Product", "Total", "Returns", "RTO", "Top Reason", "All Reasons"].map((h) => (
                                <TableCell
                                  key={h}
                                  align={["Total", "Returns", "RTO"].includes(h) ? "right" : "left"}
                                  sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                                >
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {skuBreakdown.map((row, i) => (
                              <TableRow key={i} hover>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}
                                  >
                                    {row.sellerSkuCode || row.myntraSkuCode}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {row.productId ? (
                                    <Chip label="Linked" size="small" color="success" variant="outlined" />
                                  ) : (
                                    <Chip label="Unlinked" size="small" variant="outlined" />
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight="bold">{row.total}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Chip label={row.returned} size="small" color="error" variant="outlined" />
                                </TableCell>
                                <TableCell align="right">
                                  <Chip label={row.rto} size="small" color="warning" variant="outlined" />
                                </TableCell>
                                <TableCell sx={{ maxWidth: 200 }}>
                                  {row.topReason ? (
                                    <Chip
                                      label={row.topReason}
                                      size="small"
                                      sx={{
                                        backgroundColor: `${MYNTRA_PINK}14`,
                                        color: MYNTRA_PINK,
                                        border: `1px solid ${MYNTRA_PINK}30`,
                                        maxWidth: 190,
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="body2" color="text.disabled">—</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ maxWidth: 340 }}>
                                    {row.reasons.map((r, j) => (
                                      <Chip
                                        key={j}
                                        label={`${r.reason}: ${r.count}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: 11 }}
                                      />
                                    ))}
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))}
                            {skuBreakdown.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={7} align="center">
                                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                    No data for the current filter.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{ p: 6, textAlign: "center", borderRadius: 2, borderStyle: "dashed" }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      backgroundColor: `${MYNTRA_PINK}12`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px",
                    }}
                  >
                    <ReturnIcon sx={{ fontSize: 36, color: `${MYNTRA_PINK}80` }} />
                  </Box>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No return data yet
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    Upload a Myntra Returns CSV above to populate this section.
                  </Typography>
                </Paper>
              )}
            </>
          )}
        </>
      )}

      {/* ══════════════ RECORDS TAB ══════════════ */}
      {tab === 1 && (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <CustomTable
              columns={columns}
              rows={returnReport.list}
              page={page}
              rowsPerPage={rowsPerPage}
              count={returnReport.total}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={returnReport.loading}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MyntraReturnReasonAnalysis;
